/**
 * client/ui/emotes.ts
 * Responsabilidad: Controlador de UI para:
 * 1. Pestañas de la Tienda Alquímica y Catálogo de 14 Emotes
 * 2. Sección "Mi Mochila de Emotes (X/5)" en el Perfil de Usuario
 * 3. Botón Flotante In-Game (#emote-hud-btn) y Rueda Radial (#emote-wheel-overlay)
 */

import Swal from "sweetalert2";
import {
    emoteManager,
    EMOTE_CATALOG,
    EMOTE_MAX_EQUIPPED,
    type EmoteDefinition,
} from "../emotes";
import { safeGetStorage } from "../utils";

export interface EmotesUIOptions {
    onSendEmote?: (emoteId: string) => void;
    onOpenShop?: () => void;
}

export class EmotesUI {
    private options: EmotesUIOptions;

    // Elementos de Pestañas de la Tienda
    private shopTitle = document.getElementById("shop-modal-title");
    private shopSubtitle = document.getElementById("shop-modal-subtitle");
    private tabPotionsBtn = document.getElementById("shop-tab-potions-btn");
    private tabEmotesBtn = document.getElementById("shop-tab-emotes-btn");
    private panePotions = document.getElementById("shop-pane-potions");
    private paneEmotes = document.getElementById("shop-pane-emotes");

    // Elementos de la Tienda de Emotes
    private shopCrystalsDisplay = document.getElementById("emotes-modal-crystals");
    private shopOrbsDisplay = document.getElementById("emotes-modal-orbs");
    private shopEquippedCountBadge = document.getElementById("emotes-equipped-count-badge");
    private shopWheelSlotsPreview = document.getElementById("emotes-wheel-slots-preview");
    private shopCatalogGrid = document.getElementById("emotes-catalog-grid");

    // Elementos de la Mochila en el Perfil
    private profileEquippedCountBadge = document.getElementById("profile-emotes-equipped-count");
    private profileEquippedSlots = document.getElementById("profile-equipped-slots");
    private profileUnlockedGrid = document.getElementById("profile-unlocked-emotes-grid");
    private profileOpenShopBtn = document.getElementById("profile-open-emotes-shop-btn");

    // Elementos de la Rueda Radial y HUD In-Game
    private hudBtn = document.getElementById("emote-hud-btn");
    private hudCooldownOverlay = document.getElementById("emote-hud-cooldown-overlay");
    private wheelOverlay = document.getElementById("emote-wheel-overlay");
    private wheelBackdrop = document.getElementById("emote-wheel-backdrop");
    private wheelSectorsContainer = document.getElementById("emote-wheel-sectors");

    private isWheelOpen = false;
    private selectedSectorIndex = -1;
    private activeSectorEls: HTMLElement[] = [];
    private cooldownInterval: ReturnType<typeof setInterval> | null = null;

    private cycleIndex: number = -1;
    private cycleTimer: number | null = null;
    private readonly CYCLE_TRIGGER_DELAY_MS: number = 750;

    constructor(options: EmotesUIOptions = {}) {
        this.options = options;
        this.init();
    }

    private init(): void {
        this.initShopTabs();
        this.initProfileBackpack();
        this.initRadialWheel();

        // Suscribirse a cambios reactivos de emoteManager
        emoteManager.subscribe(() => {
            this.renderShop();
            this.renderProfile();
            this.renderWheelSectors();
        });

        // Loop de cooldown para HUD button
        this.cooldownInterval = setInterval(() => {
            this.updateHUDCooldown();
        }, 100);
    }

    // =========================================================================
    // 1. Pestañas de la Tienda Alquímica
    // =========================================================================

    private initShopTabs(): void {
        if (this.tabPotionsBtn && this.tabEmotesBtn) {
            this.tabPotionsBtn.addEventListener("click", () => this.switchShopTab("potions"));
            this.tabEmotesBtn.addEventListener("click", () => this.switchShopTab("emotes"));
        }
    }

    public switchShopTab(tab: "potions" | "emotes"): void {
        if (!this.panePotions || !this.paneEmotes) return;

        if (tab === "potions") {
            this.tabPotionsBtn?.classList.add("active");
            this.tabEmotesBtn?.classList.remove("active");
            this.panePotions.classList.remove("hidden");
            this.paneEmotes.classList.add("hidden");
            if (this.shopTitle) this.shopTitle.textContent = "🧪 LA MESA DEL ALQUIMISTA";
            if (this.shopSubtitle) this.shopSubtitle.textContent = "Pociones y Consumibles Tácticos de Combate";
        } else {
            this.tabPotionsBtn?.classList.remove("active");
            this.tabEmotesBtn?.classList.add("active");
            this.panePotions.classList.add("hidden");
            this.paneEmotes.classList.remove("hidden");
            if (this.shopTitle) this.shopTitle.textContent = "😊 TIENDA DE EMOTES Y EXPRESIONES";
            if (this.shopSubtitle) this.shopSubtitle.textContent = "Colecciona y Equipa Expresiones para Batalla";
            void emoteManager.refreshStatus();
        }
    }

    // =========================================================================
    // 2. Renderizado de la Tienda de Emotes
    // =========================================================================

    private selectedRarityFilter: "all" | "common" | "rare" | "epic" = "all";
    private selectedSortOrder: "default" | "price_asc" | "price_desc" = "default";
    private currentPage: number = 1;
    private readonly pageSize: number = 8;

    private renderShopFilterBar(): void {
        if (!this.shopCatalogGrid || !this.shopCatalogGrid.parentElement) return;

        let filterBar = document.getElementById("emotes-filter-bar");
        if (!filterBar) {
            filterBar = document.createElement("div");
            filterBar.id = "emotes-filter-bar";
            filterBar.className = "shop-filter-bar";
            this.shopCatalogGrid.parentElement.insertBefore(filterBar, this.shopCatalogGrid);
        }

        filterBar.innerHTML = `
            <div class="filter-rarity-group">
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "all" ? "active" : ""}" data-rarity="all">Todos</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "common" ? "active" : ""}" data-rarity="common">Comunes</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "rare" ? "active" : ""}" data-rarity="rare">Raros</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "epic" ? "active" : ""}" data-rarity="epic">Épicos</button>
            </div>
            <select class="shop-sort-select">
                <option value="default" ${this.selectedSortOrder === "default" ? "selected" : ""}>Orden por Defecto</option>
                <option value="price_asc" ${this.selectedSortOrder === "price_asc" ? "selected" : ""}>Precio: Menor a Mayor ⬆</option>
                <option value="price_desc" ${this.selectedSortOrder === "price_desc" ? "selected" : ""}>Precio: Mayor a Menor ⬇</option>
            </select>
        `;

        filterBar.querySelectorAll<HTMLButtonElement>(".filter-pill-btn").forEach((btn) => {
            btn.addEventListener("click", () => {
                const rarity = btn.getAttribute("data-rarity") as "all" | "common" | "rare" | "epic";
                if (rarity && this.selectedRarityFilter !== rarity) {
                    this.selectedRarityFilter = rarity;
                    this.currentPage = 1;
                    this.renderShop();
                }
            });
        });

        const sortSelect = filterBar.querySelector<HTMLSelectElement>(".shop-sort-select");
        if (sortSelect) {
            sortSelect.addEventListener("change", () => {
                this.selectedSortOrder = sortSelect.value as "default" | "price_asc" | "price_desc";
                this.currentPage = 1;
                this.renderShop();
            });
        }
    }

    private renderShopPaginationBar(totalPages: number): void {
        if (!this.shopCatalogGrid || !this.shopCatalogGrid.parentElement) return;

        let paginationBar = document.getElementById("emotes-pagination-bar");
        if (!paginationBar) {
            paginationBar = document.createElement("div");
            paginationBar.id = "emotes-pagination-bar";
            paginationBar.className = "shop-pagination-bar";
            if (this.shopCatalogGrid.nextSibling) {
                this.shopCatalogGrid.parentElement.insertBefore(paginationBar, this.shopCatalogGrid.nextSibling);
            } else {
                this.shopCatalogGrid.parentElement.appendChild(paginationBar);
            }
        }

        paginationBar.innerHTML = `
            <button type="button" class="pagination-nav-btn prev-page-btn" ${this.currentPage <= 1 ? "disabled" : ""}>◀ Anterior</button>
            <span class="pagination-page-indicator">Página ${this.currentPage} de ${totalPages}</span>
            <button type="button" class="pagination-nav-btn next-page-btn" ${this.currentPage >= totalPages ? "disabled" : ""}>Siguiente ▶</button>
        `;

        const prevBtn = paginationBar.querySelector<HTMLButtonElement>(".prev-page-btn");
        const nextBtn = paginationBar.querySelector<HTMLButtonElement>(".next-page-btn");

        if (prevBtn && this.currentPage > 1) {
            prevBtn.addEventListener("click", () => {
                this.currentPage--;
                this.renderShop();
            });
        }
        if (nextBtn && this.currentPage < totalPages) {
            nextBtn.addEventListener("click", () => {
                this.currentPage++;
                this.renderShop();
            });
        }
    }

    private renderShop(): void {
        const equipped = emoteManager.getEquippedEmotes();
        const crystals = emoteManager.getCrystals();
        const orbs = emoteManager.getAstralOrbs();

        // Actualizar saldos
        if (this.shopCrystalsDisplay) {
            this.shopCrystalsDisplay.textContent = `${crystals.toLocaleString()} 💎`;
        }
        if (this.shopOrbsDisplay) {
            this.shopOrbsDisplay.textContent = `${orbs.toLocaleString()} 🔮`;
        }

        // Actualizar contador de rueda
        if (this.shopEquippedCountBadge) {
            this.shopEquippedCountBadge.textContent = `${equipped.length}/${EMOTE_MAX_EQUIPPED}`;
            this.shopEquippedCountBadge.classList.toggle("full", equipped.length >= EMOTE_MAX_EQUIPPED);
        }

        // Renderizar casillas equipadas en la cabecera
        if (this.shopWheelSlotsPreview) {
            this.shopWheelSlotsPreview.innerHTML = "";
            const catalogList = emoteManager.getCatalogList();
            for (let i = 0; i < EMOTE_MAX_EQUIPPED; i++) {
                const emoteId = equipped[i];
                const slot = document.createElement("div");
                slot.className = `emote-slot-preview ${emoteId ? "filled" : "empty"}`;

                const def = emoteId ? (catalogList.find((e) => e.id === emoteId) || EMOTE_CATALOG[emoteId]) : null;
                if (emoteId && def) {
                    slot.title = `${def.name} (Toca para desequipar)`;
                    const img = document.createElement("img");
                    img.src = def.svgUrl;
                    img.alt = def.name;
                    img.className = "emote-slot-img";
                    slot.appendChild(img);

                    slot.addEventListener("click", () => {
                        this.handleUnequip(emoteId);
                    });
                } else {
                    slot.textContent = "—";
                    slot.title = `Ranura ${i + 1} vacía`;
                }

                const num = document.createElement("span");
                num.className = "emote-slot-num";
                num.textContent = `${i + 1}`;
                slot.appendChild(num);

                this.shopWheelSlotsPreview.appendChild(slot);
            }
        }

        // Renderizar catálogo de emotes filtrado, ordenado y paginado (8 por página)
        if (this.shopCatalogGrid) {
            this.renderShopFilterBar();
            this.shopCatalogGrid.innerHTML = "";
            const isFull = equipped.length >= EMOTE_MAX_EQUIPPED;
            const catalog = emoteManager.getCatalogList();

            let filteredItems = [...catalog];
            if (this.selectedRarityFilter !== "all") {
                filteredItems = filteredItems.filter((item) => {
                    if (this.selectedRarityFilter === "common") return item.tier === "common";
                    if (this.selectedRarityFilter === "rare") return item.tier === "rare";
                    if (this.selectedRarityFilter === "epic") return item.tier === "exclusive" || (item.tier as string) === "epic";
                    return true;
                });
            }

            if (this.selectedSortOrder === "price_asc") {
                filteredItems.sort((a, b) => {
                    const costA = a.currency === "orbs" ? a.price * 1000 : a.price;
                    const costB = b.currency === "orbs" ? b.price * 1000 : b.price;
                    return costA - costB;
                });
            } else if (this.selectedSortOrder === "price_desc") {
                filteredItems.sort((a, b) => {
                    const costA = a.currency === "orbs" ? a.price * 1000 : a.price;
                    const costB = b.currency === "orbs" ? b.price * 1000 : b.price;
                    return costB - costA;
                });
            }

            const totalPages = Math.max(1, Math.ceil(filteredItems.length / this.pageSize));
            if (this.currentPage > totalPages) {
                this.currentPage = totalPages;
            }
            const pagedItems = filteredItems.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);

            for (const def of pagedItems) {
                const id = def.id;
                const isUnlocked = emoteManager.isUnlocked(id);
                const isEquipped = emoteManager.isEquipped(id);

                const card = document.createElement("div");
                card.className = `emote-card tier-${def.tier}`;

                // Tier badge
                const tierBadge = document.createElement("div");
                tierBadge.className = `emote-card-tier-badge badge-${def.tier}`;
                tierBadge.textContent = def.tier === "exclusive" ? "Exclusivo 🔮" : def.tier === "rare" ? "Raro" : "Común";
                card.appendChild(tierBadge);

                // Icon / SVG
                const iconWrap = document.createElement("div");
                iconWrap.className = "emote-card-icon-wrap";
                const img = document.createElement("img");
                img.src = def.svgUrl;
                img.alt = def.name;
                img.className = "emote-card-svg";
                img.loading = "lazy";
                iconWrap.appendChild(img);
                card.appendChild(iconWrap);

                // Name
                const name = document.createElement("div");
                name.className = "emote-card-name";
                name.textContent = def.name;
                name.title = def.name;
                card.appendChild(name);

                // Action button
                const btn = document.createElement("button");
                btn.type = "button";

                if (!isUnlocked) {
                    btn.className = `emote-card-btn btn-buy ${def.currency === "orbs" ? "btn-buy-orbs" : ""}`;
                    btn.innerHTML = `<span>Comprar</span> <span>${def.price} ${def.currency === "orbs" ? "🔮" : "💎"}</span>`;
                    btn.addEventListener("click", () => this.handleBuy(id));
                } else if (isEquipped) {
                    btn.className = "emote-card-btn btn-equipped";
                    btn.textContent = "Equipado ✓ (Quitar)";
                    btn.addEventListener("click", () => this.handleUnequip(id));
                } else {
                    btn.className = "emote-card-btn btn-equip";
                    btn.textContent = isFull ? "Rueda Llena (5/5)" : "Equipar";
                    btn.disabled = isFull;
                    btn.addEventListener("click", () => this.handleEquip(id));
                }

                card.appendChild(btn);
                this.shopCatalogGrid.appendChild(card);
            }

            this.renderShopPaginationBar(totalPages);
        }
    }

    // =========================================================================
    // 3. Renderizado de "Mi Mochila de Emotes" en el Perfil
    // =========================================================================

    private initProfileBackpack(): void {
        if (this.profileOpenShopBtn) {
            this.profileOpenShopBtn.addEventListener("click", () => {
                // Cerrar primero el modal de cuenta para evitar solapamiento de modales
                document.getElementById("account-modal")?.classList.add("hidden");

                if (this.options.onOpenShop) {
                    this.options.onOpenShop();
                } else {
                    const potionsModal = document.getElementById("potions-modal");
                    if (potionsModal) {
                        potionsModal.classList.remove("hidden");
                    }
                }
                this.switchShopTab("emotes");
            });
        }
    }

    private renderProfile(): void {
        const equipped = emoteManager.getEquippedEmotes();
        const unlocked = emoteManager.getUnlockedEmotes();

        // Contador
        if (this.profileEquippedCountBadge) {
            this.profileEquippedCountBadge.textContent = `${equipped.length}/${EMOTE_MAX_EQUIPPED}`;
            this.profileEquippedCountBadge.classList.toggle("full", equipped.length >= EMOTE_MAX_EQUIPPED);
        }

        // 5 Ranuras de la rueda
        if (this.profileEquippedSlots) {
            this.profileEquippedSlots.innerHTML = "";
            const catalogList = emoteManager.getCatalogList();
            for (let i = 0; i < EMOTE_MAX_EQUIPPED; i++) {
                const emoteId = equipped[i];
                const slot = document.createElement("div");
                slot.className = `profile-equipped-slot ${emoteId ? "filled" : "empty"}`;

                const def = emoteId ? (catalogList.find((e) => e.id === emoteId) || EMOTE_CATALOG[emoteId]) : null;
                if (emoteId && def) {
                    slot.title = `${def.name} (Toca para desequipar)`;
                    const img = document.createElement("img");
                    img.src = def.svgUrl;
                    img.alt = def.name;
                    img.className = "emote-slot-img";
                    slot.appendChild(img);

                    slot.addEventListener("click", () => {
                        this.handleUnequip(emoteId);
                    });
                } else {
                    slot.textContent = "—";
                    slot.title = `Ranura ${i + 1} vacía`;
                }

                this.profileEquippedSlots.appendChild(slot);
            }
        }

        // Cuadrícula de emotes desbloqueados en la mochila
        if (this.profileUnlockedGrid) {
            this.profileUnlockedGrid.innerHTML = "";
            const catalogList = emoteManager.getCatalogList();

            if (unlocked.length === 0) {
                const emptyMsg = document.createElement("div");
                emptyMsg.className = "profile-empty-emotes-msg";
                emptyMsg.textContent = "Aún no tienes emotes desbloqueados. ¡Visita la tienda para obtenerlos!";
                this.profileUnlockedGrid.appendChild(emptyMsg);
            } else {
                for (const id of unlocked) {
                    const def = catalogList.find((e) => e.id === id) || EMOTE_CATALOG[id];
                    if (!def) continue;
                    const isEquipped = equipped.includes(id);

                    const item = document.createElement("div");
                    item.className = `profile-inventory-emote-item ${isEquipped ? "is-equipped" : ""}`;
                    item.title = `${def.name} (${isEquipped ? "Equipado - Toca para quitar" : "Toca para equipar"})`;

                    const img = document.createElement("img");
                    img.src = def.svgUrl;
                    img.alt = def.name;
                    img.className = "emote-slot-img";
                    item.appendChild(img);

                    item.addEventListener("click", () => {
                        if (isEquipped) {
                            this.handleUnequip(id);
                        } else {
                            this.handleEquip(id);
                        }
                    });

                    this.profileUnlockedGrid.appendChild(item);
                }
            }
        }
    }

    // =========================================================================
    // 4. Manejo de Acciones: Compra, Equipar y Desequipar
    // =========================================================================

    private async handleBuy(emoteId: string): Promise<void> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) {
            void Swal.fire({
                title: "Inicia Sesión Requerido",
                text: "Para adquirir emotes y guardarlos en tu cuenta de Alchem.io, inicia sesión o regístrate.",
                icon: "info",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#38bdf8",
                background: "#0f172a",
                color: "#f8fafc",
            });
            return;
        }

        const def = emoteManager.getCatalogList().find((e) => e.id === emoteId) || EMOTE_CATALOG[emoteId];
        if (!def) return;

        const res = await emoteManager.buyEmote(emoteId);
        if (res.success) {
            void Swal.fire({
                title: `¡${def.name} adquirido!`,
                text: `Se ha añadido a tu colección de emotes${(res.equippedEmotes || []).includes(emoteId) ? " y se ha equipado en tu rueda." : "."}`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
                background: "#0f172a",
                color: "#f8fafc",
            });
        } else {
            void Swal.fire({
                title: "No se pudo comprar",
                text: res.message || "Ocurrió un error al procesar la compra.",
                icon: "warning",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#f59e0b",
                background: "#0f172a",
                color: "#f8fafc",
            });
        }
    }

    private async handleEquip(emoteId: string): Promise<void> {
        const equipped = emoteManager.getEquippedEmotes();
        if (equipped.includes(emoteId)) return;

        if (equipped.length >= EMOTE_MAX_EQUIPPED) {
            void Swal.fire({
                title: "Rueda de Emotes Llena",
                text: "Ya tienes 5 emotes equipados. Quita uno antes de equipar este.",
                icon: "info",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#38bdf8",
                background: "#0f172a",
                color: "#f8fafc",
            });
            return;
        }

        const newEquipped = [...equipped, emoteId];
        await emoteManager.equipEmotes(newEquipped);
    }

    private async handleUnequip(emoteId: string): Promise<void> {
        const equipped = emoteManager.getEquippedEmotes();
        const newEquipped = equipped.filter((id) => id !== emoteId);
        await emoteManager.equipEmotes(newEquipped);
    }

    // =========================================================================
    // 5. Botón Flotante In-Game (#emote-hud-btn) y Rueda Radial
    // =========================================================================

    private initRadialWheel(): void {
        // Clic / Tap en el botón flotante in-game
        if (this.hudBtn) {
            this.hudBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.cycleNextEmote();
            });
        }

        // Clic en el backdrop para cerrar
        if (this.wheelBackdrop) {
            this.wheelBackdrop.addEventListener("click", () => {
                this.closeWheel();
            });
        }

        // Listener de Teclado (Tecla 'T' para PC con ciclado y disparo tras 750ms)
        window.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "t" || e.key === "T") {
                const activeTag = document.activeElement?.tagName.toLowerCase();
                if (activeTag === "input" || activeTag === "textarea") return;
                const isAnyModalOpen =
                    document.getElementById("alias-modal")?.classList.contains("hidden") === false ||
                    document.getElementById("potions-modal")?.classList.contains("hidden") === false ||
                    document.getElementById("account-modal")?.classList.contains("hidden") === false;
                if (isAnyModalOpen) return;

                e.preventDefault();
                this.cycleNextEmote();
            } else if (e.key === "Escape" && this.isWheelOpen) {
                this.closeWheel();
            }
        });

        // Seguimiento del cursor respecto al centro de la rueda para resaltar por ángulo
        window.addEventListener("mousemove", (e: MouseEvent) => {
            if (!this.isWheelOpen || !this.wheelOverlay) return;

            const rect = this.wheelOverlay.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = e.clientX - cx;
            const dy = e.clientY - cy;
            const dist = Math.hypot(dx, dy);

            // Zona muerta central de 38px
            if (dist < 38) {
                this.highlightSector(-1);
                return;
            }

            // Calcular ángulo normalizado [0, 2*PI] partiendo desde -PI/2 (arriba)
            let angle = Math.atan2(dy, dx);
            // Rotar para alinear con el sector 0 que está arriba (-PI/2)
            angle = (angle + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);

            const sectorAngle = (Math.PI * 2) / EMOTE_MAX_EQUIPPED; // 72 grados
            const index = Math.floor((angle + sectorAngle / 2) % (Math.PI * 2) / sectorAngle);
            this.highlightSector(index);
        });

        // Soporte táctil deslizante en móvil
        window.addEventListener("touchmove", (e: TouchEvent) => {
            if (!this.isWheelOpen || !this.wheelOverlay || e.touches.length === 0) return;
            const touch = e.touches[0];
            const rect = this.wheelOverlay.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = touch.clientX - cx;
            const dy = touch.clientY - cy;
            const dist = Math.hypot(dx, dy);

            if (dist < 35) {
                this.highlightSector(-1);
                return;
            }

            let angle = Math.atan2(dy, dx);
            angle = (angle + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
            const sectorAngle = (Math.PI * 2) / EMOTE_MAX_EQUIPPED;
            const index = Math.floor((angle + sectorAngle / 2) % (Math.PI * 2) / sectorAngle);
            this.highlightSector(index);
        }, { passive: true });

        window.addEventListener("touchend", () => {
            if (this.isWheelOpen && this.selectedSectorIndex >= 0) {
                const equipped = emoteManager.getEquippedEmotes();
                const emoteId = equipped[this.selectedSectorIndex];
                if (emoteId) {
                    this.triggerEmote(emoteId);
                }
                this.closeWheel();
            }
        });
    }

    public cycleNextEmote(): void {
        const equipped = emoteManager.getEquippedEmotes().filter(Boolean) as string[];
        if (equipped.length === 0) return;

        // Cancelar temporizador previo si sigue pulsando 'T'
        if (this.cycleTimer !== null) {
            window.clearTimeout(this.cycleTimer);
        }

        // Avanzar índice
        this.cycleIndex = (this.cycleIndex + 1) % equipped.length;
        this.selectedSectorIndex = this.cycleIndex;

        // Abrir o mantener la rueda mostrando el sector seleccionado
        if (!this.isWheelOpen) {
            this.openWheel();
        }
        this.highlightSector(this.selectedSectorIndex);

        // Programar disparo tras 750ms de inactividad
        this.cycleTimer = window.setTimeout(() => {
            const targetEmoteId = equipped[this.cycleIndex];
            if (targetEmoteId) {
                this.triggerEmote(targetEmoteId);
            }
            this.closeWheel();
            this.cycleIndex = -1;
            this.cycleTimer = null;
        }, this.CYCLE_TRIGGER_DELAY_MS);
    }

    public openWheel(): void {
        if (!this.wheelOverlay) return;
        this.isWheelOpen = true;
        this.selectedSectorIndex = -1;
        this.wheelOverlay.classList.remove("hidden");
        this.renderWheelSectors();
    }

    public closeWheel(): void {
        if (this.cycleTimer !== null) {
            window.clearTimeout(this.cycleTimer);
            this.cycleTimer = null;
        }
        this.cycleIndex = -1;
        if (!this.wheelOverlay) return;
        this.isWheelOpen = false;
        this.selectedSectorIndex = -1;
        this.wheelOverlay.classList.add("hidden");
        this.highlightSector(-1);
    }

    public toggleWheel(): void {
        if (this.isWheelOpen) {
            this.closeWheel();
        } else {
            this.openWheel();
        }
    }

    public setHUDButtonVisible(visible: boolean): void {
        if (this.hudBtn) {
            this.hudBtn.classList.toggle("hidden", !visible);
        }
        if (!visible && this.isWheelOpen) {
            this.closeWheel();
        }
    }

    private updateHUDCooldown(): void {
        if (!this.hudCooldownOverlay) return;
        const remainingMs = emoteManager.getCooldownRemainingMs();
        if (remainingMs > 0) {
            this.hudCooldownOverlay.classList.remove("hidden");
            this.hudCooldownOverlay.textContent = `${(remainingMs / 1000).toFixed(1)}s`;
        } else {
            this.hudCooldownOverlay.classList.add("hidden");
        }
    }

    private renderWheelSectors(): void {
        if (!this.wheelSectorsContainer) return;
        this.wheelSectorsContainer.innerHTML = "";
        this.activeSectorEls = [];

        const equipped = emoteManager.getEquippedEmotes();
        const radius = 100; // Radio del pentágono en píxeles

        for (let i = 0; i < EMOTE_MAX_EQUIPPED; i++) {
            // Empezar en -PI/2 (arriba) y avanzar en sentido horario
            const angle = -Math.PI / 2 + (i * 2 * Math.PI) / EMOTE_MAX_EQUIPPED;
            const x = Math.round(140 + Math.cos(angle) * radius);
            const y = Math.round(140 + Math.sin(angle) * radius);

            const emoteId = equipped[i];
            const sector = document.createElement("div");
            sector.style.left = `${x}px`;
            sector.style.top = `${y}px`;

            const def = emoteId ? (emoteManager.getCatalogList().find((e) => e.id === emoteId) || EMOTE_CATALOG[emoteId]) : null;
            if (emoteId && def) {
                sector.className = `emote-wheel-sector tier-${def.tier}`;
                sector.title = def.name;

                const img = document.createElement("img");
                img.src = def.svgUrl;
                img.alt = def.name;
                sector.appendChild(img);

                sector.addEventListener("click", (e) => {
                    e.stopPropagation();
                    this.triggerEmote(emoteId);
                    this.closeWheel();
                });
            } else {
                sector.className = "emote-wheel-sector empty";
                sector.textContent = "—";
                sector.title = "Ranura no asignada";
            }

            this.wheelSectorsContainer.appendChild(sector);
            this.activeSectorEls.push(sector);
        }
    }

    private highlightSector(index: number): void {
        this.selectedSectorIndex = index;
        for (let i = 0; i < this.activeSectorEls.length; i++) {
            const el = this.activeSectorEls[i];
            if (i === index) {
                el.classList.add("active-hover");
            } else {
                el.classList.remove("active-hover");
            }
        }
    }

    private triggerEmote(emoteId: string): void {
        if (!emoteManager.canSendEmote()) {
            return; // Cooldown de 3s
        }
        emoteManager.recordEmoteSent();
        this.options.onSendEmote?.(emoteId);
    }
}
